const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const formatCurrency = (amount) =>
  new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(Number(amount) || 0);

const getVatAmount = (subtotal, vat) =>
  Math.round((subtotal * (Number(vat) || 0)) / 100);

const renderTotalCell = (amount, vatAmount) => `
  <td class="text-right">
    <div>${formatCurrency(amount)}</div>
    ${
      vatAmount > 0
        ? `<div class="vat-note">VAT: ${formatCurrency(vatAmount)}</div>`
        : ""
    }
  </td>
`;

const buildServiceRows = (services) =>
  services
    .map((service, index) => {
      const price = Number(service.price) || 0;
      const licenseFee = Number(service.licenseFee) || 0;
      const quantity = Number(service.quantity) || 1;
      const subtotal = (price + licenseFee) * quantity;
      const vatAmount = getVatAmount(subtotal, service.vat);

      return `
        <tr class="${service.isChild ? "service-detail-row" : "service-main-row"}">
          <td class="text-center">${service.isChild ? "" : index + 1}</td>
          <td>${escapeHtml(service.name)}</td>
          <td class="text-center">${quantity}</td>
          <td class="text-right">${formatCurrency(price)}</td>
          <td class="text-right">${formatCurrency(licenseFee)}</td>
          <td class="text-center">${Number(service.vat) || 0}%</td>
          ${renderTotalCell(subtotal + vatAmount, vatAmount)}
        </tr>
      `;
    })
    .join("");

const buildAdjustmentRows = (adjustments = []) =>
  adjustments
    .filter((adjustment) => adjustment?.reason?.trim())
    .map(
      (adjustment, index) => `
        <tr>
          <td class="text-center">${index + 1}</td>
          <td>${escapeHtml(adjustment.reason.trim())}</td>
          <td class="text-center">${escapeHtml(adjustment.type || "")}</td>
          <td class="text-right">${formatCurrency(adjustment.amount)}</td>
        </tr>
      `,
    )
    .join("");

export const buildQuotationBodyHTML = ({
  date,
  specialist = "",
  email = "",
  phone = "",
  services,
  adjustments = [],
  subtotal,
  vatTotal,
  grandTotal,
}) => `
  <h1 class="heading">BÁO GIÁ DỊCH VỤ</h1>
  <div class="info-section">
    <div class="info-row"><span class="info-label">Ngày:</span><span>${escapeHtml(date)}</span></div>
    <div class="info-row"><span class="info-label">Chuyên viên:</span><span>${escapeHtml(specialist)}</span></div>
    <div class="info-row"><span class="info-label">Email:</span><span>${escapeHtml(email)}</span></div>
    <div class="info-row"><span class="info-label">Số điện thoại:</span><span>${escapeHtml(phone)}</span></div>
    <div class="info-row"><span class="info-label">Nền tảng:</span><a href="https://sohuutritue.vn/" class="info-link">Sohuutritue.vn</a></div>
  </div>
  <table>
    <thead>
      <tr>
        <th class="col-index">STT</th>
        <th>Tên dịch vụ</th>
        <th class="col-quantity">SL</th>
        <th class="col-money">Phí đăng ký</th>
        <th class="col-money">Phí cấp bằng</th>
        <th class="col-vat">VAT</th>
        <th class="col-money">Thành tiền</th>
      </tr>
    </thead>
    <tbody>${buildServiceRows(services)}</tbody>
  </table>
  ${
    adjustments.length > 0
      ? `<div class="adjustment-section">
          <div class="adjustment-title">Thông tin điều chỉnh</div>
          <table class="adjustment-table">
            <thead>
              <tr>
                <th class="adjustment-index-col">STT</th>
                <th>Nội dung điều chỉnh</th>
                <th class="adjustment-type-col">Loại điều chỉnh</th>
                <th class="adjustment-amount-col">Số tiền</th>
              </tr>
            </thead>
            <tbody>${buildAdjustmentRows(adjustments)}</tbody>
          </table>
        </div>`
      : ""
  }
  <div class="summary-section">
    <div class="summary-row"><span>Tạm tính:</span><strong>${formatCurrency(subtotal)}</strong></div>
    ${
      vatTotal > 0
        ? `<div class="summary-row"><span>Tổng VAT:</span><strong>${formatCurrency(vatTotal)}</strong></div>`
        : ""
    }
    <div class="total-row"><strong>TỔNG CỘNG:</strong><strong class="total-amount">${formatCurrency(grandTotal)}</strong></div>
  </div>
`;

export const buildQuotationPrintHTML = (
  bodyHTML,
  { regularFontBase64 = "", boldFontBase64 = "" } = {},
) => {
  const exportedDate = new Date().toLocaleDateString("vi-VN");

  return `<!doctype html>
  <html lang="vi">
  <head>
    <meta charset="utf-8"/>
    <title>Báo giá dịch vụ</title>
    <style>
      ${regularFontBase64 ? `@font-face{font-family:"Quotation Serif";src:url("${regularFontBase64}") format("truetype");font-weight:400}` : ""}
      ${boldFontBase64 ? `@font-face{font-family:"Quotation Serif";src:url("${boldFontBase64}") format("truetype");font-weight:600 900}` : ""}
      :root{--page-w:794px;--page-h:1123px}
      *{box-sizing:border-box;margin:0;padding:0;font-family:"Quotation Serif","Times New Roman",serif!important}
      html,body{background:#fff;color:#111}
      @page{size:794px 1123px;margin:0}
      body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .pdf-root{position:relative;z-index:1;width:var(--page-w);min-height:var(--page-h);margin:0 auto;padding:40px 48px;line-height:1.45;font-size:13px}
      .heading{text-align:center;font-size:20px;font-weight:700;padding-bottom:8px;margin-bottom:18px}
      .info-section{margin-bottom:16px}
      .info-row{display:flex;margin-bottom:4px}
      .info-label{font-weight:700;width:112px}
      a{color:#0267ff;text-decoration:none}
      .info-link{font-weight:600}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-top:18px}
      thead{display:table-header-group}
      tr{break-inside:avoid;page-break-inside:avoid}
      th,td{border:1px solid #222;padding:7px;vertical-align:middle}
      th{font-weight:700;text-align:center}
      .text-center{text-align:center}.text-right{text-align:right}
      .col-index{width:44px}.col-quantity{width:48px}.col-vat{width:54px}.col-money{width:104px}
      .service-main-row td{padding-top:9px;padding-bottom:9px;font-weight:600}
      .service-detail-row td{font-size:10px}
      .service-detail-row td:nth-child(2){padding-left:18px}
      .child-prefix{display:inline-block;width:14px;font-weight:700}
      .vat-note{margin-top:2px;font-size:8px;color:#444;font-weight:400}
      .adjustment-section{margin-top:14px;page-break-inside:avoid}
      .adjustment-title{margin-bottom:6px;font-size:12px;font-weight:700}
      .adjustment-table{margin-top:0;font-size:11px}
      .adjustment-table th,.adjustment-table td{padding:7px;white-space:pre-wrap}
      .adjustment-index-col{width:44px}
      .adjustment-type-col{width:130px}
      .adjustment-amount-col{width:140px}
      .summary-section{margin-top:16px;page-break-inside:avoid}
      .summary-row{display:flex;justify-content:space-between;margin-bottom:7px;font-size:12px}
      .total-row{display:flex;justify-content:space-between;padding-top:4px;margin-top:4px;font-size:14px}
      .total-amount{font-size:17px}
      .quotation-footer{margin-top:26px;text-align:center;font-size:10px;line-height:1.35;color:#333;page-break-inside:avoid}
      .wm-overlay-fixed{position:fixed;inset:0;padding:30px 52px;background-size:95%;background-repeat:no-repeat;background-position:center;background-origin:content-box;opacity:.12;pointer-events:none;z-index:999999}
    </style>
  </head>
  <body>
    <div class="pdf-root">
      ${bodyHTML}
      <div class="quotation-footer">
        <p>Báo giá được xuất ngày ${exportedDate} từ Nền tảng số Sohuutritue.vn</p>
        <p>Tổng đài tư vấn Sở hữu trí tuệ: <a href="tel:0819009697">08 1900 9697</a> (điện thoại, zalo) - Email: <a href="mailto:luatsu@sohuutritue.vn">luatsu@sohuutritue.vn</a></p>
      </div>
    </div>
    <div class="wm-overlay-fixed"></div>
  </body>
  </html>`;
};
